/**
 * Shared control styling and small display pieces for the Student Plans mockup
 * (ENG-10000).
 *
 * ⚠️ MOCKUP SUPPORT. Lives beside `StudentPlans.tsx` rather than in a general
 * components folder so the whole feature can be deleted in one move if the
 * shape does not survive review.
 *
 * The classes are lifted verbatim from `ScheduledPasses.tsx`, which took them
 * from the pass-type editor. Three hall-pass settings pages already agree on
 * what an input and a chip look like, and a fourth inventing its own is how a
 * section starts to look assembled rather than designed.
 */

import type { ReactNode } from 'react';
import { ShieldAlert, BadgeCheck, CalendarClock } from 'lucide-react';
import { Tooltip } from '@/components/Tooltip';
import { countLabel } from '@/lib/studentPlans';

/** Text / number / select fields, matching the pass-type editor. */
export const inputCls =
  'rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500';

/** A pill-shaped toggle, on or off. Used for the restriction-flavor switch. */
export const chipCls = (on: boolean) =>
  `rounded-full border px-3 py-1.5 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 ${
    on
      ? 'border-primary-500 bg-primary-500 text-white hover:bg-primary-600'
      : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
  }`;

/**
 * Solid blue, matching "New Limit" on Pass Limits and "New Group" on No Meetup.
 * Adding is the primary act of every one of these pages.
 */
export const ADD_BUTTON_CLASS =
  'inline-flex h-[38px] flex-shrink-0 items-center justify-center gap-1.5 rounded-lg bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2';

/** The quieter "Add …" that sits on a section header inside the detail view. */
export const SECTION_ADD_BUTTON_CLASS =
  'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-semibold text-primary-600 transition-colors hover:bg-primary-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500';

/**
 * Per-row icon actions. 44px on touch, 28px on a pointer, exactly as the
 * scheduled-pass rows do it: two controls sit close together and one of them
 * deletes, so the fingertip case gets the size both platform guidelines land on
 * (SC 2.5.5).
 *
 * Carries no hover colour of its own; each action supplies one.
 */
export const ROW_BUTTON_CLASS =
  'inline-flex h-11 w-11 sm:h-7 sm:w-7 flex-shrink-0 items-center justify-center rounded-md text-gray-500 transition-colors focus:outline-none focus-visible:ring-2';
export const ROW_ICON_CLASS = 'h-5 w-5 sm:h-4 sm:w-4';
// Edit rests BLUE, matching the pencils on Set Pass Limits and No-Pass
// Schedules (text-primary-600 / hover:bg-primary-50).
export const HOVER_EDIT = 'text-primary-600 hover:bg-primary-50';
export const HOVER_DELETE = 'hover:bg-red-50 hover:text-red-700';

/** Cancel / Save, matching the scheduled-pass editor's footer pair. */
export const CANCEL_BUTTON_CLASS =
  'rounded-md px-3 py-2 text-sm font-semibold text-gray-600 transition-colors hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500';
export const SAVE_BUTTON_CLASS =
  'rounded-md bg-primary-600 px-3.5 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';

/**
 * A labelled block in a modal body. A plain `<label>` cannot own a group of
 * controls, and half of these fields are pickers that carry their own
 * accessible name, so the visible label is a heading and the group is described
 * by it rather than pointed at it.
 */
export function Field({
  label,
  hint,
  htmlFor,
  labelAdornment,
  children,
}: {
  label: string;
  hint?: ReactNode;
  /** Set only where the control really is a single native field. */
  htmlFor?: string;
  /** Sits after the label, OUTSIDE the <label> element — an interactive
   *  adornment (a help tooltip's button) inside a label would forward clicks
   *  to the field. */
  labelAdornment?: ReactNode;
  children: ReactNode;
}) {
  const labelCls = 'block text-sm font-semibold text-gray-700';
  const labelEl = htmlFor ? (
    <label className={labelCls} htmlFor={htmlFor}>
      {label}
    </label>
  ) : (
    <div className={labelCls}>{label}</div>
  );
  return (
    <div>
      {labelAdornment ? (
        <span className="mb-1 flex items-center gap-1.5">
          {labelEl}
          {labelAdornment}
        </span>
      ) : (
        <span className="mb-1 block">{labelEl}</span>
      )}
      {children}
      {hint && <p className="mt-1.5 text-sm text-gray-500">{hint}</p>}
    </div>
  );
}

/**
 * The two counts on a list row.
 *
 * Restrictions are red-tinted and accommodations blue, and the pair is the
 * whole reason the list exists: a leader scanning it is asking "who is capped
 * and who is exempt", and colour answers that before the number is read. Both
 * tints are the 100/800 tier, which measures 7.9:1 and 8.6:1 — the 50/700 pair
 * used elsewhere on this page is too faint to separate at a glance.
 */
/** The plan-page icon vocabulary, keyed by tone. One table per glyph: the
 *  same trio anchors the detail page's section headers, so a pill here and
 *  the section it counts always wear the same icon. */
const PILL_META = {
  // Scheduled is neutral gray on purpose: it is operational fact, not a rule
  // with a valence, and a third loud color would dilute the red/blue pair.
  restriction: { Icon: ShieldAlert, iconCls: 'text-red-500', noun: 'Restriction' },
  accommodation: { Icon: BadgeCheck, iconCls: 'text-blue-500', noun: 'Accommodation' },
  scheduled: { Icon: CalendarClock, iconCls: 'text-gray-400', noun: 'Scheduled pass' },
} as const;

/** Icon + count, with the full "2 Restrictions" carried for assistive tech and
 *  as a hover tooltip. No lozenge: at this size a row of filled pills read as
 *  clutter, so the colored icon carries the tone and the count sits beside it
 *  as quiet text. The icon trio matches the detail page's section headers,
 *  which is where the vocabulary is taught. */
export function CountPill({
  tone,
  count,
}: {
  tone: keyof typeof PILL_META;
  count: number;
}) {
  const { Icon, iconCls, noun } = PILL_META[tone];
  const full = countLabel(count, noun);
  // A real tooltip rather than a native `title`: the icons are the vocabulary
  // and a first-time reader should not have to hold still for a second to
  // learn it. Screen readers get the sr-only copy either way.
  return (
    <Tooltip title={full} position="top" delay={350}>
      <span className="inline-flex flex-shrink-0 items-center gap-1 text-xs font-semibold text-gray-600">
        <Icon className={`h-4 w-4 ${iconCls}`} aria-hidden="true" />
        <span aria-hidden="true">{count}</span>
        <span className="sr-only">{full}</span>
      </span>
    </Tooltip>
  );
}
